import type { Map as MapboxMap } from 'mapbox-gl';
import { setBasemapSatellite } from './basemap';

const MapboxSecret = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN;

export class SatelliteToggleControl {
  private _container: HTMLElement | null = null;
  private _isSatellite = false;
  private _previewImg!: HTMLImageElement;
  private map!: MapboxMap;
  private debounceTimer: number | null = null;
  private _toggle = 0;

  onAdd(map: MapboxMap): HTMLElement {
    this.map = map;

    // Control container
    this._container = document.createElement('div');
    this._container.className =
      'pointer-events-auto h-16 w-16 border border-white rounded-xl bg-white shadow-[0_4px_8px_0_#00000026] ml-4 p-1 cursor-pointer hover:bg-neutral-100';

    // Preview image
    this._previewImg = document.createElement('img');
    this._previewImg.alt = 'Toggle Satellite';
    this._previewImg.title = 'Toggle Satellite';
    this._previewImg.className = 'h-full w-full object-cover rounded-lg';

    // update preview immediately
    this.updatePreview();

    // and whenever the map moves
    this.map.on('moveend', this.debouncedUpdate);

    this._container.onclick = () => this.handleOnClick();

    this._container.appendChild(this._previewImg);
    return this._container;
  }

  handleOnClick = () => {
    if (!this.map) return;
    const next = !this._isSatellite;
    const toggle = ++this._toggle;
    const apply = () => {
      if (toggle !== this._toggle) return;
      if (!setBasemapSatellite(this.map, next)) return;
      this._isSatellite = next;
      this.updatePreview();
    };
    apply();
    if (this._isSatellite !== next) this.map.once('idle', apply);
  };

  private debouncedUpdate = () => {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    this.debounceTimer = window.setTimeout(() => {
      this.updatePreview();
      this.debounceTimer = null;
    }, 500); // wait 500ms after last move before fetching
  };

  // Make the preview image
  private updatePreview = () => {
    if (!this.map) return;
    const center = this.map.getCenter();
    const zoom = this.map.getZoom();
    const lng = center.lng.toFixed(6);
    const lat = center.lat.toFixed(6);

    // Show preview of the opposite style - if currently satellite, show light preview
    const style = this._isSatellite ? 'streets-v12' : 'satellite-v9';
    const url = `https://api.mapbox.com/styles/v1/mapbox/${style}/static/${lng},${lat},${zoom}/200x200?access_token=${MapboxSecret}`;

    this._previewImg.src = url;
  };

  onRemove(): void {
    this.map.off('moveend', this.debouncedUpdate);
    if (this._container?.parentNode) {
      this._container.parentNode.removeChild(this._container);
    }
  }
}
